import HttpService from "./http";

export type PartnerProgram =
  | "distributor"
  | "affiliate"
  | "distributor_pending"
  | "unset"
  | "unreadable";

export type ClientSubscriptionStatus =
  | "ACTIVE"
  | "UNPAID"
  | "ENDED"
  | "NONE"
  | "UNKNOWN"
  | "DELETED";

export interface PartnerSummary {
  partner_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  company_name: string | null;
  program: PartnerProgram;
  business_number: string | null;
  contract_signed_at: string | null;
  attached_clients: number | null;
  detached_clients: number | null;
  active_clients: number | null;
  milestone: number | null;
}

export interface PartnerClient {
  entity_id: string;
  entity_name: string | null;
  owner_email: string | null;
  plan_code: string | null;
  plan_tier: string | null;
  status: ClientSubscriptionStatus;
  renewal_date: string | null;
  ever_paid: boolean | null;
  registration_date_time: string | null;
  attached_by: {
    user_id: string;
    at: string;
    reason: string | null;
    forced: boolean;
    warnings?: string[];
  } | null;
  detached_at: string | null;
  detached_by: string | null;
  detached_reason: string | null;
  /** Distributors only: the plan it pays for this client. */
  sponsored?: SponsoredPlan | null;
  legal_identifier?: string | null;
}

export interface SponsoredPlan {
  subscription_id: string;
  plan_code: string;
  plan_tier: string | null;
  running: boolean;
  auto_renew: boolean | null;
  current_period_end: string | null;
  provider_status: string | null;
  started_at: string | null;
}

export type LedgerState = "pending" | "due" | "on_hold" | "canceled" | "paid";

export interface LedgerLine {
  id: string;
  program: "distributor" | "affiliate";
  kind: "rebate" | "commission" | "clawback";
  client_entity_id: string;
  plan_code: string | null;
  plan_tier?: string | null;
  interval?: string | null;
  subscription_id?: string | null;
  invoice_id?: string | null;
  paid_at?: string | null;
  amount: number;
  currency: string;
  state: LedgerState;
  due_on?: string | null;
  upgrade?: boolean;
  upgrade_settled?: boolean;
  cancel_reason?: string | null;
  held_by?: string | null;
  clawback_of?: string | null;
  payout_id?: string | null;
  paid_on?: string | null;
  accounting_document?: string | null;
  created_at: string;
  source?: string | null;
  author?: string | null;
  reason?: string | null;
  canceled_by?: string | null;
}

export interface NewLedgerLine {
  entity_identification: string;
  kind: "commission";
  amount: number;
  currency: string;
  paid_on: string;
  reason: string;
  plan_code?: string;
  subscription_id?: string;
  invoice_id?: string;
}

export interface LedgerBalance {
  pending: number;
  due: number;
  on_hold: number;
  paid: number;
  canceled: number;
  payable: boolean;
  threshold: number;
}

export type PartnerPayoutMethod = "bank_transfer" | "stripe_connect" | "paypal";

export interface PartnerPayout {
  id: string;
  paymod_platform_payout_id: string | null;
  method?: PartnerPayoutMethod;
  currency: string;
  amount: number;
  executed_on: string;
  reference: string | null;
  accounting_document: string | null;
  line_ids: string[];
  reversed_at?: string | null;
  destination: {
    holder?: string | null;
    iban?: string;
    stripe_account_id?: string;
    paypal_email?: string;
  };
  author: string | null;
  created_at: string;
}

export interface PartnerStatement {
  lines: LedgerLine[];
  balances: Record<string, LedgerBalance>;
  payouts: PartnerPayout[];
}

export type StripeConnectStatus =
  | "PENDING"
  | "RESTRICTED"
  | "ACTIVE"
  | "INACTIVE"
  | "DISABLED"
  | "REJECTED";

export interface PayoutDestination {
  method: string | null;
  bank: {
    holder: string | null;
    bank_name: string | null;
    iban: string;
    bic: string | null;
  } | null;
  stripe_connect: {
    status: StripeConnectStatus;
    stripe_account_id: string;
  } | null;
  paypal: { address: string } | null;
}

export interface UpgradeSettlement {
  old_subscription_id: string;
  old_plan_code: string;
  new_subscription_id: string;
  new_plan_code: string;
  entity_id: string;
  partner_id: string;
  status: "pending" | "settled";
  created_at: string;
  suggestion?: {
    invoice_id: string;
    amount_paid: number;
    currency: string;
    period_start: string;
    period_end: string;
    days_left: number;
    suggested_refund: number;
  } | null;
  refund_id?: string | null;
  amount_refunded?: number;
  currency?: string | null;
  reason?: string;
  settled_at?: string;
  settled_by?: string;
}

export type ContractStatus =
  | "pending"
  | "viewed"
  | "review"
  | "signed"
  | "declined"
  | "expired"
  | "cancelled"
  | "not_sent"
  | "not_distributor_contract"
  | "unavailable";

export interface PartnerDetail extends PartnerSummary {
  clients: PartnerClient[];
  identity: {
    birth_date: string | null;
    phone: string | null;
    country: string | null;
  };
  address: {
    line1: string | null;
    line2: string | null;
    zipcode: string | null;
    city: string | null;
    state: string | null;
  };
  follow_up: { partner_level: string | null; is_validated: boolean };
  payout: {
    method: string | null;
    bank: {
      holder: string | null;
      bank_name: string | null;
      iban: string | null;
      bic: string | null;
    } | null;
    paypal: { address: string } | null;
  };
  contract: {
    status: ContractStatus;
    requested_at: string | null;
    sent_at: string | null;
    viewed_at: string | null;
    expires_at: string | null;
    signed_at: string | null;
    has_signed_pdf: boolean;
  } | null;
}

export type AttachWarningCode =
  | "ALREADY_PAID"
  | "PAYMENT_HISTORY_UNKNOWN"
  | "ATTACHED_ELSEWHERE";

export interface AttachWarning {
  code: AttachWarningCode;
  plan_code?: string;
  partner_id?: string;
}

export interface AttachResult {
  attached: boolean;
  warnings: AttachWarning[];
}

const base = (customerId: string, entityId: string) =>
  `/${customerId}/${entityId}/partners`;

const PartnerAdminService = {
  list: async (customerId: string, entityId: string) =>
    (await HttpService.get(base(customerId, entityId))).data
      .element as PartnerSummary[],

  get: async (customerId: string, entityId: string, partnerId: string) =>
    (
      await HttpService.get(
        `${base(customerId, entityId)}/${encodeURIComponent(partnerId)}`
      )
    ).data.element as PartnerDetail,

  signedContract: async (
    customerId: string,
    entityId: string,
    partnerId: string
  ) =>
    (
      await HttpService.get(
        `${base(customerId, entityId)}/${encodeURIComponent(
          partnerId
        )}/contract`
      )
    ).data.element as { url: string; signedAt: string | null },

  /** Resolves with the warnings when the back end asks for confirmation (HTTP 409). */
  attach: async (
    customerId: string,
    entityId: string,
    partnerId: string,
    body: { entity_identification: string; reason?: string; force?: boolean }
  ): Promise<AttachResult> => {
    try {
      const response = await HttpService.post(
        `${base(customerId, entityId)}/${encodeURIComponent(partnerId)}/attach`,
        body
      );
      return response.data.element as AttachResult;
    } catch (error: any) {
      if (error?.response?.status === 409) {
        return error.response.data.element as AttachResult;
      }
      throw error;
    }
  },

  statement: async (customerId: string, entityId: string, partnerId: string) =>
    (
      await HttpService.get(
        `${base(customerId, entityId)}/${encodeURIComponent(partnerId)}/ledger`
      )
    ).data.element as PartnerStatement,

  reconcile: async (customerId: string, entityId: string, partnerId: string) =>
    (
      await HttpService.post(
        `${base(customerId, entityId)}/${encodeURIComponent(
          partnerId
        )}/ledger/reconcile`,
        {}
      )
    ).data.element as {
      created: number;
      canceled: number;
      held: number;
      released: number;
      reopened: number;
    },

  /** A line root adds by hand; the same form sent twice answers the line already recorded. */
  addLine: async (
    customerId: string,
    entityId: string,
    partnerId: string,
    body: NewLedgerLine
  ) =>
    (
      await HttpService.post(
        `${base(customerId, entityId)}/${encodeURIComponent(
          partnerId
        )}/ledger/lines`,
        body
      )
    ).data.element as LedgerLine,

  /** Withdraws a line root added: cancelled, or taken back from the next payout once paid. */
  cancelLine: async (
    customerId: string,
    entityId: string,
    partnerId: string,
    lineId: string
  ) =>
    (
      await HttpService.post(
        `${base(customerId, entityId)}/${encodeURIComponent(
          partnerId
        )}/ledger/lines/${encodeURIComponent(lineId)}/cancel`,
        {}
      )
    ).data.element as LedgerLine,

  payoutDestination: async (
    customerId: string,
    entityId: string,
    partnerId: string
  ) =>
    (
      await HttpService.get(
        `${base(customerId, entityId)}/${encodeURIComponent(
          partnerId
        )}/payout-destination`
      )
    ).data.element as PayoutDestination,

  pay: async (
    customerId: string,
    entityId: string,
    partnerId: string,
    body: {
      currency: string;
      method: PartnerPayoutMethod;
      executed_on?: string;
      reference?: string;
      accounting_document?: string;
      expected_amount: number;
    }
  ) =>
    (
      await HttpService.post(
        `${base(customerId, entityId)}/${encodeURIComponent(
          partnerId
        )}/payouts`,
        body
      )
    ).data.element as PartnerPayout,

  settlements: async (customerId: string, entityId: string) =>
    (await HttpService.get(`${base(customerId, entityId)}/settlements`)).data
      .element as UpgradeSettlement[],

  settle: async (
    customerId: string,
    entityId: string,
    oldSubscriptionId: string,
    body: { refund_amount: number; reason: string }
  ) =>
    (
      await HttpService.post(
        `${base(customerId, entityId)}/settlements/${encodeURIComponent(
          oldSubscriptionId
        )}`,
        body
      )
    ).data.element as UpgradeSettlement,

  detach: async (
    customerId: string,
    entityId: string,
    partnerId: string,
    body: {
      entity_identification: string;
      reason: string;
      stop_subscription?: boolean;
    }
  ) =>
    (
      await HttpService.post(
        `${base(customerId, entityId)}/${encodeURIComponent(partnerId)}/detach`,
        body
      )
    ).data.element as { detached: boolean; subscription_stopped?: boolean },
};

export default PartnerAdminService;
